import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/client';

const STATUS_COLORS = {
  open:        { bg: '#dbeafe', color: '#1d4ed8' },
  in_progress: { bg: '#fef9c3', color: '#92400e' },
  closed:      { bg: '#f3f4f6', color: '#6b7280' },
  resolved:    { bg: '#dcfce7', color: '#166534' },
};
const PRIORITY_COLORS = {
  low:    { bg: '#f0fdf4', color: '#166534' },
  normal: { bg: '#f3f4f6', color: '#374151' },
  high:   { bg: '#fff7ed', color: '#c2410c' },
  urgent: { bg: '#fef2f2', color: '#b91c1c' },
};
const ROLE_LABELS = {
  private_person: 'Customer', vehicle_owner: 'Vehicle Owner',
  driver: 'Driver', bodaboda: 'Boda-Boda',
  hotel_host: 'Hotel Host', event_host: 'Event Host', admin: 'Admin',
};

function Chip({ value, map, label }) {
  const style = (map && map[value]) || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: style.bg, color: style.color, whiteSpace: 'nowrap' }}>
      {label || value}
    </span>
  );
}

function fmt(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const LIMIT = 30;

export default function SupportTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets]     = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [filterStatus, setFS]     = useState('');
  const [filterPriority, setFP]   = useState('');
  const [filterCategory, setFC]   = useState('');
  // Transferred tickets live in Case Management (Kayd, 26.10.2026); this
  // switch shows them here read-only for reference.
  const [showTransferred, setST]  = useState(false);
  const [search, setSearch]       = useState('');
  const [searchInput, setSI]      = useState('');
  const [selected, setSelected]   = useState(null);  // full ticket object
  const [messages, setMessages]   = useState([]);
  const [detailLoading, setDL]    = useState(false);
  const [replyText, setReply]     = useState('');
  const [replying, setReplying]   = useState(false);
  const [transferReason, setTR]   = useState('');
  const [transferring, setTrans]  = useState(false);
  const [showTransfer, setShowTr] = useState(false);
  // On narrow screens show detail as a full overlay instead of side panel
  const [narrow, setNarrow]       = useState(window.innerWidth < 900);

  useEffect(() => {
    const h = () => setNarrow(window.innerWidth < 900);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: LIMIT });
      if (filterStatus)   p.set('status',   filterStatus);
      if (filterPriority) p.set('priority', filterPriority);
      if (filterCategory) p.set('category', filterCategory);
      if (search)         p.set('search',   search);
      if (showTransferred) p.set('transferred', 'only');
      const res = await api.get(`/api/support/admin/tickets?${p}`);
      setTickets(res.data.tickets || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not load tickets');
    } finally { setLoading(false); }
  }, [page, filterStatus, filterPriority, filterCategory, search, showTransferred]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (ticket) => {
    setSelected(ticket);
    setReply('');
    setDL(true);
    try {
      const res = await api.get(`/api/support/admin/tickets/${ticket.id}`);
      setSelected({ ...res.data.ticket, case: res.data.case || null });
      setMessages(res.data.messages || []);
    } catch { toast.error('Could not load ticket details'); }
    finally { setDL(false); }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      await api.post(`/api/support/admin/tickets/${selected.id}/reply`, { message: replyText.trim() });
      setReply('');
      toast.success('Reply sent');
      openDetail(selected);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to send reply'); }
    finally { setReplying(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/api/support/admin/tickets/${id}/status`, { status });
      toast.success(`Status → ${status}`);
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const doTransfer = async () => {
    if (transferring) return; // one request at a time — never a duplicate case
    setTrans(true);
    try {
      const res = await api.post(`/api/support/admin/tickets/${selected.id}/transfer-to-case`, { reason: transferReason.trim() || undefined });
      toast.success(res.data.already_transferred
        ? `${res.data.ticket_number} is already case ${res.data.case?.case_number}`
        : `${res.data.ticket_number} transferred — case ${res.data.case?.case_number}`);
      setShowTr(false); setTR('');
      // The ticket now lives in Case Management and leaves this list.
      const ticketId = selected.id;
      setSelected(null); setMessages([]);
      load();
      toast((tt) => (
        <span>
          Now in Case Management.{' '}
          <button style={s.toastLink} onClick={() => { toast.dismiss(tt.id); navigate(`/case-control/support/${ticketId}`); }}>Open case →</button>
        </span>
      ), { duration: 8000 });
    } catch (err) { toast.error(err.response?.data?.error || 'Transfer failed'); }
    finally { setTrans(false); }
  };

  const applySearch = () => { setSearch(searchInput); setPage(1); };
  const resetAll    = () => { setFS(''); setFP(''); setFC(''); setSearch(''); setSI(''); setPage(1); };
  const totalPages  = Math.ceil(total / LIMIT);
  const hasFilters  = filterStatus || filterPriority || filterCategory || search;

  // Shared detail panel content
  const DetailContent = selected ? (
    <div style={s.detail}>
      {/* Header */}
      <div style={s.detailTop}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <span style={s.tktNum}>{selected.ticket_number || '—'}</span>
          <h2 style={s.detailSubject}>{selected.subject}</h2>
        </div>
        <button style={s.closeBtn} onClick={() => setSelected(null)} aria-label="Close">✕</button>
      </div>

      {/* Meta */}
      <div style={s.metaGrid}>
        <Row label="Status">
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip value={selected.status} map={STATUS_COLORS} label={(selected.status || 'open').replace('_', ' ')} />
            <select style={s.inlineSelect} value={selected.status || 'open'} onChange={(e) => updateStatus(selected.id, e.target.value)}>
              {['open','in_progress','resolved','closed'].map((v) => <option key={v} value={v}>{v.replace('_',' ')}</option>)}
            </select>
          </div>
        </Row>
        <Row label="Priority"><Chip value={selected.priority} map={PRIORITY_COLORS} /></Row>
        <Row label="Category">{selected.category || '—'}</Row>
        <Row label="Created">{fmt(selected.created_at)}</Row>
        {selected.resolved_at && <Row label="Resolved">{fmt(selected.resolved_at)}</Row>}
      </div>

      {/* Submitter */}
      {selected.users && (
        <div style={s.userCard}>
          <div style={s.userCardHead}>Submitted by</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 2 }}>
            <span style={s.userName}>{selected.users.full_name || selected.users.email}</span>
            <Chip value={selected.users.user_role} map={{}} label={ROLE_LABELS[selected.users.user_role] || selected.users.user_role} />
          </div>
          <div style={s.userMeta}>{selected.users.email}</div>
          {selected.users.customer_number && <div style={s.userMeta}>Customer #{selected.users.customer_number}</div>}
        </div>
      )}

      {/* Case link */}
      {selected.case_control_case_id && (
        <div style={s.caseLink}>
          ✅ Transferred to Case Management{selected.case?.case_number ? ` — case ${selected.case.case_number}` : ''}{selected.transferred_to_case_at ? ` · ${fmt(selected.transferred_to_case_at)}` : ''}
          {selected.case?.transferred_by_name ? ` · by ${selected.case.transferred_by_name}` : ''}
          {selected.case?.reason ? <div style={{ fontWeight: 400, marginTop: 4 }}>Reason: {selected.case.reason}</div> : null}
          <div style={{ marginTop: 6 }}><Link to={`/case-control/support/${selected.id}`} style={{ color: 'inherit', fontWeight: 700 }}>Open the case in Case Management →</Link></div>
        </div>
      )}

      {/* Transfer button */}
      {!selected.case_control_case_id && (
        <button style={s.transferBtn} onClick={() => setShowTr(true)}>📁 Transfer to Case Management</button>
      )}

      {/* Transfer modal */}
      {showTransfer && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <h3 style={s.modalTitle}>Transfer to Case Management</h3>
            <p style={s.modalDesc}>Original ticket number, message history, and attachments will be preserved and linked to the new case.</p>
            <textarea style={s.textarea} placeholder="Reason (optional)" value={transferReason} onChange={(e) => setTR(e.target.value)} rows={3} />
            <div style={s.modalActions}>
              <button style={s.primaryBtn} disabled={transferring} onClick={doTransfer}>{transferring ? 'Transferring…' : 'Transfer'}</button>
              <button style={s.secondaryBtn} onClick={() => { setShowTr(false); setTR(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div style={s.msgSection}>
        <h3 style={s.msgTitle}>Messages</h3>
        {detailLoading ? <p style={s.hint}>Loading…</p> : messages.length === 0 ? <p style={s.hint}>No messages yet.</p> : (
          <div style={s.msgList}>
            {messages.map((m) => (
              <div key={m.id} style={{ ...s.bubble, ...(m.sender_role === 'admin' ? s.adminBubble : s.userBubble), ...(m.is_internal_note ? s.noteBubble : {}) }}>
                <div style={s.bubbleMeta}>
                  <span style={s.bubbleRole}>{m.is_internal_note ? '🔒 Internal note' : m.sender_role === 'admin' ? '🛡 Admin' : m.sender_role === 'system' ? '⚙ System' : '👤 User'}</span>
                  <span style={s.bubbleTime}>{fmt(m.created_at)}</span>
                </div>
                <div style={s.bubbleText}>{m.message}</div>
              </div>
            ))}
          </div>
        )}
        <div style={s.replyBox}>
          <textarea style={s.replyInput} placeholder="Write a reply…" value={replyText} onChange={(e) => setReply(e.target.value)} rows={3} />
          <button style={s.sendBtn} disabled={!replyText.trim() || replying} onClick={sendReply}>{replying ? 'Sending…' : 'Send reply'}</button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div style={s.page}>
      <h1 style={s.h1}>Support Tickets</h1>
      <p style={s.intro}>All requests from customers, drivers, vehicle owners, and hosts.</p>

      {/* Filters */}
      <div style={s.filtersRow}>
        <div style={s.searchWrap}>
          <input style={s.searchInput} placeholder="Search by ticket number or subject…"
            value={searchInput} onChange={(e) => setSI(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applySearch()} />
          <button style={s.searchBtn} onClick={applySearch}>Search</button>
        </div>
        <select style={s.sel} value={filterStatus} onChange={(e) => { setFS(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select style={s.sel} value={filterPriority} onChange={(e) => { setFP(e.target.value); setPage(1); }}>
          <option value="">All priorities</option>
          <option value="low">Low</option>
          <option value="normal">Normal</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
        <select style={s.sel} value={filterCategory} onChange={(e) => { setFC(e.target.value); setPage(1); }}>
          <option value="">All categories</option>
          {['account','booking','payment','vehicle','ride','accommodation','other'].map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select style={s.sel} value={showTransferred ? 'only' : ''} onChange={(e) => { setST(e.target.value === 'only'); setSelected(null); setPage(1); }} aria-label="Ticket list">
          <option value="">Active tickets</option>
          <option value="only">Transferred to Case Management</option>
        </select>
        {hasFilters && <button style={s.resetBtn} onClick={resetAll}>Reset</button>}
      </div>

      {/* Body: list + optional side detail (wide) OR list then detail pushes down (narrow) */}
      <div style={{ ...s.body, ...(selected && !narrow ? s.bodyWithPanel : {}) }}>
        {/* Ticket list */}
        <div style={s.listCol}>
          {loading ? <p style={s.hint}>Loading…</p> : tickets.length === 0 ? (
            <p style={s.hint}>{showTransferred ? 'No transferred tickets' : 'No tickets'}{hasFilters ? ' matching filters' : ''}.</p>
          ) : (
            <>
              <p style={s.count}>{total} ticket{total !== 1 ? 's' : ''}</p>
              {tickets.map((t) => (
                <div key={t.id}
                  style={{ ...s.card, ...(selected?.id === t.id ? s.cardActive : {}) }}
                  onClick={() => openDetail(t)} role="button" tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && openDetail(t)}>
                  <div style={s.cardTop}>
                    <span style={s.tktNum}>{t.ticket_number || '—'}</span>
                    <Chip value={t.status} map={STATUS_COLORS} label={(t.status || 'open').replace('_', ' ')} />
                  </div>
                  <div style={s.cardSubject}>{t.subject}</div>
                  <div style={s.cardMeta}>
                    {t.users?.full_name || t.users?.email || 'Unknown'}
                    {t.users?.user_role ? ` · ${ROLE_LABELS[t.users.user_role] || t.users.user_role}` : ''}
                    {t.users?.customer_number ? ` · #${t.users.customer_number}` : ''}
                  </div>
                  <div style={s.cardBottom}>
                    <Chip value={t.priority} map={PRIORITY_COLORS} />
                    {t.category && <span style={s.catChip}>{t.category}</span>}
                    {t.case_control_case_id && <span style={s.caseChip}>Case</span>}
                    <span style={s.dateText}>{fmt(t.created_at)}</span>
                  </div>
                </div>
              ))}
              {totalPages > 1 && (
                <div style={s.pager}>
                  <button style={s.pageBtn} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
                  <span style={s.hint}>Page {page}/{totalPages}</span>
                  <button style={s.pageBtn} disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next →</button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Side panel on wide screens */}
        {selected && !narrow && (
          <div style={s.sidePanel}>{DetailContent}</div>
        )}
      </div>

      {/* Full-screen overlay on narrow screens */}
      {selected && narrow && (
        <div style={s.mobileOverlay}>{DetailContent}</div>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6, fontSize: 13 }}>
      <span style={{ color: '#6b7280', minWidth: 76, flexShrink: 0, fontWeight: 600, paddingTop: 1 }}>{label}</span>
      <span style={{ color: '#111827', minWidth: 0 }}>{children}</span>
    </div>
  );
}

const s = {
  page:         { padding: '20px 16px', boxSizing: 'border-box', maxWidth: 1400 },
  h1:           { fontSize: 22, fontWeight: 800, margin: '0 0 4px' },
  intro:        { fontSize: 13.5, color: '#4b5563', margin: '0 0 16px' },
  filtersRow:   { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' },
  searchWrap:   { display: 'flex', flex: '1 1 200px', maxWidth: 360 },
  searchInput:  { flex: 1, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '8px 0 0 8px', fontSize: 13, outline: 'none', minWidth: 0 },
  searchBtn:    { padding: '7px 12px', border: '1px solid #d1d5db', borderLeft: 'none', borderRadius: '0 8px 8px 0', background: '#f9fafb', cursor: 'pointer', fontSize: 13, fontWeight: 600, flexShrink: 0 },
  sel:          { padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12.5, background: '#fff', cursor: 'pointer', minWidth: 100 },
  resetBtn:     { padding: '7px 12px', borderRadius: 8, border: 'none', background: '#fee2e2', color: '#991b1b', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  body:         { display: 'flex', gap: 16, alignItems: 'flex-start' },
  bodyWithPanel:{ /* side-by-side handled by sidePanel flex */ },
  listCol:      { flex: '1 1 300px', minWidth: 0 },
  count:        { fontSize: 12.5, color: '#6b7280', margin: '0 0 8px' },
  card:         { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '11px 13px', marginBottom: 8, cursor: 'pointer', boxSizing: 'border-box' },
  cardActive:   { border: '1.5px solid #0B6A4A', background: '#f0fdf4' },
  cardTop:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 6 },
  tktNum:       { fontSize: 11, fontWeight: 700, fontFamily: 'monospace', color: '#374151', background: '#f3f4f6', padding: '1px 7px', borderRadius: 5, flexShrink: 0 },
  cardSubject:  { fontSize: 13.5, fontWeight: 700, color: '#111827', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardMeta:     { fontSize: 11.5, color: '#6b7280', marginBottom: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardBottom:   { display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' },
  catChip:      { padding: '2px 7px', borderRadius: 999, background: '#f3f4f6', color: '#374151', fontSize: 10.5, fontWeight: 600 },
  caseChip:     { padding: '2px 7px', borderRadius: 999, background: '#ede9fe', color: '#6d28d9', fontSize: 10.5, fontWeight: 700 },
  dateText:     { fontSize: 11, color: '#9ca3af', marginLeft: 'auto' },
  pager:        { display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 },
  pageBtn:      { padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12.5 },
  hint:         { color: '#9ca3af', fontSize: 13 },
  sidePanel:    { flex: '0 0 440px', maxWidth: 440, position: 'sticky', top: 16, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto', boxSizing: 'border-box' },
  mobileOverlay:{ position: 'fixed', inset: 0, background: '#fff', zIndex: 200, overflowY: 'auto' },
  detail:       { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 14px', boxSizing: 'border-box' },
  detailTop:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 8 },
  detailSubject:{ fontSize: 15, fontWeight: 800, margin: '4px 0 0', color: '#111827', lineHeight: 1.3 },
  closeBtn:     { background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#9ca3af', padding: '2px 4px', flexShrink: 0 },
  metaGrid:     { marginBottom: 14 },
  inlineSelect: { padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  userCard:     { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 12 },
  userCardHead: { fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', marginBottom: 5 },
  userName:     { fontSize: 14, fontWeight: 700, color: '#111827' },
  userMeta:     { fontSize: 12, color: '#6b7280', marginTop: 2 },
  caseLink:     { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '7px 10px', borderRadius: 8, fontSize: 12.5, marginBottom: 10 },
  transferBtn:  { width: '100%', padding: '8px 12px', background: '#fff', border: '1.5px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12, textAlign: 'left' },
  overlay:      { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400 },
  modal:        { background: '#fff', borderRadius: 14, padding: '20px 18px', maxWidth: 420, width: '92%', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' },
  modalTitle:   { fontSize: 16, fontWeight: 800, margin: '0 0 6px' },
  modalDesc:    { fontSize: 13, color: '#4b5563', margin: '0 0 12px', lineHeight: 1.5 },
  textarea:     { width: '100%', minHeight: 70, padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13.5, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' },
  modalActions: { display: 'flex', gap: 8, marginTop: 10 },
  primaryBtn:   { padding: '8px 16px', background: '#0B6A4A', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  secondaryBtn: { padding: '8px 12px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 13 },
  msgSection:   { marginTop: 14, borderTop: '1px solid #f3f4f6', paddingTop: 14 },
  msgTitle:     { fontSize: 13.5, fontWeight: 700, margin: '0 0 10px', color: '#111827' },
  msgList:      { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 },
  bubble:       { padding: '9px 12px', borderRadius: 10 },
  adminBubble:  { background: '#eff6ff', border: '1px solid #bfdbfe' },
  noteBubble:   { background: '#fffbeb', border: '1px dashed #f59e0b' },
  toastLink:    { border: 'none', background: 'none', color: '#0B6A4A', fontWeight: 700, cursor: 'pointer', padding: 0, fontSize: 'inherit' },
  userBubble:   { background: '#f9fafb', border: '1px solid #e5e7eb' },
  bubbleMeta:   { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 3 },
  bubbleRole:   { fontSize: 11, fontWeight: 700, color: '#374151' },
  bubbleTime:   { fontSize: 10.5, color: '#9ca3af' },
  bubbleText:   { fontSize: 13, color: '#111827', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  replyBox:     { display: 'flex', flexDirection: 'column', gap: 8 },
  replyInput:   { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13.5, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' },
  sendBtn:      { alignSelf: 'flex-end', padding: '8px 16px', background: '#0B6A4A', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
};
